import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Package, Users, Truck, FileText, BarChart3,
  LogOut, Menu, X, ChevronDown, FolderTree, Layers, UserCheck, UserPlus,
  ClipboardList, ShoppingCart, Receipt, RotateCcw, FileSearch, Settings, Calendar,
  Building2, ChevronsUpDown, Check, Upload, Banknote, Calculator, TrendingUp, Send, Megaphone, Plus, CheckSquare, FileDown,
  ChevronsLeft, ChevronsRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { authAPI, adminAPI, tenantAPI, taskAPI } from "@/lib/api";
import GlobalSearch from "@/components/GlobalSearch";
import { TaskButton, TaskPanel } from "@/components/TaskPanel";
import SessionInfo from "@/components/SessionInfo";
import { toast } from "sonner";
import { useSidebarCollapsed, useSidebarAutoCollapse } from "@/lib/sidebarCollapse";
import "@/App.css";

const ALL_DOC_CHILDREN = [
  { to: "/documents?type=devis", label: "Devis", icon: FileText, types: ["devis_facture"] },
  { to: "/documents?type=proforma", label: "Devis", icon: FileText, types: ["proforma_commande_facture"] },
  { to: "/documents?type=commande", label: "Commandes", icon: ShoppingCart, types: ["proforma_commande_facture"] },
  { to: "/documents?type=facture", label: "Factures", icon: Receipt, types: ["devis_facture", "proforma_commande_facture"] },
  { to: "/documents?type=avoir", label: "Avoirs", icon: RotateCcw, types: ["devis_facture", "proforma_commande_facture"] },
];

function getNavItems(workflow, modules, isSuperadmin, tenantRole) {
  const docChildren = ALL_DOC_CHILDREN.filter(c => c.types.includes(workflow));
  const items = [
    { to: "/", icon: LayoutDashboard, label: "Tableau de bord" },
  ];

  // Factures à établir right after dashboard (requires contrats module)
  if (modules.includes("facturation_prev") && modules.includes("contrats")) {
    items.push({ to: "/facturation", icon: Calendar, label: "Factures à établir" });
  }

  items.push(
    {
      icon: Package, label: "Produits",
      children: [
        { to: "/produits", label: "Tous les produits", icon: Package },
        { to: "/produits?tab=families", label: "Familles", icon: FolderTree },
        { to: "/produits?tab=sub-families", label: "Sous-familles", icon: Layers },
      ]
    },
    {
      icon: Users, label: "Clients",
      children: [
        { to: "/clients", label: "Tous", icon: Users },
        { to: "/clients?type=client", label: "Clients", icon: UserCheck },
        { to: "/clients?type=prospect", label: "Prospects", icon: UserPlus },
        ...docChildren,
      ]
    },
  );

  // Module: fournisseurs
  if (modules.includes("fournisseurs")) {
    items.push({
      icon: Truck, label: "Fournisseurs",
      children: [
        { to: "/fournisseurs", label: "Tous", icon: Truck },
        { to: "/fournisseurs/commandes", label: "Commandes", icon: ShoppingCart },
        { to: "/factures-fournisseurs", label: "Factures", icon: Receipt },
        ...(modules.includes("ia") ? [{ to: "/fournisseurs/devis-pdf", label: "Devis PDF (IA)", icon: FileSearch }] : []),
      ]
    });
  }

  // Notes de frais (independant entry, visible to all tenant users)
  items.push({ to: "/notes-de-frais", icon: Receipt, label: "Notes de frais" });

  // Module: stats (with TVA and Marges as sub-menus)
  if (modules.includes("stats")) {
    items.push({
      icon: BarChart3, label: "Statistiques",
      children: [
        { to: "/statistiques", label: "Vue générale", icon: BarChart3 },
        { to: "/tva", label: "TVA", icon: Calculator },
        { to: "/marges-produits", label: "Marge produit", icon: TrendingUp },
      ]
    });
  }

  // Relevé bancaire
  if (modules.includes("facturation_prev")) {
    items.push({ to: "/relevé-bancaire", icon: Banknote, label: "Relevé bancaire" });
  }

  // Module: export
  if (modules.includes("export")) {
    items.push({ to: "/exports", icon: FileDown, label: "Exports" });
  }

  // Module: comptabilite
  if (modules.includes("comptabilite")) {
    items.push({ to: "/comptabilite", icon: Calculator, label: "Comptabilité" });
  }

  // Module: emailing (Marketing)
  if (modules.includes("emailing")) {
    items.push({
      icon: Megaphone, label: "Marketing",
      children: [
        { to: "/marketing/campagnes", label: "Campagnes emails", icon: Send },
        { to: "/marketing/listes", label: "Listes de diffusion", icon: Users },
        { to: "/marketing/templates", label: "Templates", icon: FileText },
      ]
    });
  }

  // Import donnees: only for admin/owner/superadmin
  return items;
}

function isItemActive(item, pathname, search) {
  if (item.to) {
    const [itemPath, itemQuery] = item.to.split("?");
    if (itemQuery) {
      return pathname === itemPath && search.includes(itemQuery);
    }
    return pathname === itemPath && !search;
  }
  if (item.children) {
    return item.children.some(c => {
      const [cp] = c.to.split("?");
      return pathname === cp || pathname.startsWith(cp + "/");
    });
  }
  return false;
}

function TenantSwitcher({ user, onSwitch }) {
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const tenants = user?.tenants || [];
  const currentTenantId = user?.current_tenant_id;
  const currentTenant = tenants.find(t => t.id === currentTenantId);

  // Only show for superadmin with multiple tenants
  if (!user?.is_superadmin || tenants.length <= 1) return null;

  const handleSelect = async (tenantId) => {
    if (tenantId === currentTenantId || switching) return;
    setSwitching(true);
    try {
      await tenantAPI.select(tenantId);
      setOpen(false);
      if (onSwitch) onSwitch();
    } catch (err) {
      console.error("Tenant switch error:", err);
      toast.error("Erreur changement d'espace");
      setSwitching(false);
    }
  };

  return (
    <div className="px-4 py-3 border-b border-slate-700" data-testid="tenant-switcher">
      <button
        onClick={() => tenants.length > 1 && setOpen(!open)}
        className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors ${tenants.length > 1 ? "hover:bg-slate-800 cursor-pointer" : "cursor-default"}`}
        data-testid="tenant-switcher-btn"
      >
        <div className="w-7 h-7 rounded-md bg-indigo-600/20 flex items-center justify-center flex-shrink-0">
          <Building2 size={14} className="text-indigo-400" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-xs font-medium text-white truncate">{currentTenant?.name || "Aucun tenant"}</p>
          <p className="text-[10px] text-slate-500 truncate">{currentTenant?.slug || ""}</p>
        </div>
        {tenants.length > 1 && <ChevronsUpDown size={14} className="text-slate-500 flex-shrink-0" />}
      </button>
      {open && tenants.length > 1 && (
        <div className="mt-1.5 rounded-lg bg-slate-800 border border-slate-700 overflow-hidden" data-testid="tenant-switcher-dropdown">
          {tenants.map(t => (
            <button
              key={t.id}
              onClick={() => handleSelect(t.id)}
              disabled={switching}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors text-xs ${t.id === currentTenantId ? "bg-indigo-600/10 text-indigo-300" : "text-slate-300 hover:bg-slate-700"} ${switching ? "opacity-50" : ""}`}
              data-testid={`tenant-option-${t.slug}`}
            >
              <Building2 size={12} />
              <span className="flex-1 truncate">{t.name}</span>
              {t.id === currentTenantId && <Check size={12} className="text-indigo-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NavContent({ user, onLogout, onClose, navItems, onTenantSwitch, appName, brandLogo, collapsed = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [expanded, setExpanded] = useState({});
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const pathname = location.pathname;
  const search = location.search;

  const toggleExpand = (label) => {
    setExpanded(prev => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className="flex flex-col h-full" data-testid="sidebar-nav">
      {collapsed ? (
        <div className="py-4 flex justify-center border-b border-slate-700">
          <img src="/logo_komptai_dark.svg" alt="KomptAI" className="w-8 h-8 object-contain" />
        </div>
      ) : (
      <div className="p-6 border-b border-slate-700">
        {brandLogo ? (
          <img src={`${BACKEND_URL}${brandLogo}`} alt="" className="h-8 object-contain" data-testid="sidebar-brand-logo" />
        ) : (
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {appName || <img src="/logo_komptai_dark.svg" alt="KomptAI" className="w-[80%] object-contain" />}
          </h1>
        )}
      </div>
      )}
      {!collapsed && <TenantSwitcher user={user} onSwitch={onTenantSwitch} />}
      {!collapsed && (
      <div className="px-4 pt-3 pb-1">
        <GlobalSearch />
      </div>
      )}
      {/* Mobile Quick Create */}
      <div className="lg:hidden px-3 pt-2">
        <button
          onClick={() => setQuickCreateOpen(!quickCreateOpen)}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold transition-colors"
          data-testid="mobile-quick-create-btn"
        >
          <Plus size={14} strokeWidth={2.5} />
          <span>Nouveau</span>
          <ChevronDown size={12} className={`ml-auto transition-transform ${quickCreateOpen ? "rotate-180" : ""}`} />
        </button>
        {quickCreateOpen && (
          <div className="mt-1 space-y-0.5 bg-slate-800/50 rounded-lg p-2">
            {[
              { to: "/documents?type=facture&new=1", label: "Facture", icon: Receipt, color: "text-emerald-400" },
              { to: "/documents?type=devis&new=1", label: "Devis", icon: FileText, color: "text-indigo-400" },
              { to: "/clients?new=1", label: "Client", icon: Users, color: "text-sky-400" },
              { to: "/produits?new=1", label: "Produit", icon: Package, color: "text-violet-400" },
              { to: "/fournisseurs?new=supplier", label: "Fournisseur", icon: Truck, color: "text-orange-400" },
              { to: "/notes-de-frais?new=1", label: "Note de frais", icon: Receipt, color: "text-rose-400" },
            ].map(item => (
              <button
                key={item.to}
                onClick={() => { navigate(item.to); onClose?.(); setQuickCreateOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded text-xs text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors"
                data-testid={`mobile-qc-${item.label.toLowerCase()}`}
              >
                <item.icon size={13} className={item.color} />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <nav className={`flex-1 space-y-0.5 overflow-y-auto ${collapsed ? "p-2" : "p-4"}`}>
        {navItems.map((item) => {
          // Replié : une seule icône par entrée ; un parent mène à son premier enfant.
          if (collapsed) {
            const to = item.to || item.children?.[0]?.to || "/";
            const actif = isItemActive(item, pathname, search);
            return (
              <NavLink
                key={item.label}
                to={to}
                end={to === "/"}
                onClick={onClose}
                title={item.label}
                className={`flex items-center justify-center h-10 w-full rounded-lg transition-colors ${actif ? "bg-indigo-600/20 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}
                data-testid={`nav-icon-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon size={18} strokeWidth={1.5} />
              </NavLink>
            );
          }
          if (item.children) {
            const isParentActive = isItemActive(item, pathname, search);
            const isOpen = expanded[item.label] !== undefined ? expanded[item.label] : isParentActive;
            return (
              <div key={item.label}>
                <button
                  onClick={() => toggleExpand(item.label)}
                  className={`sidebar-link w-full ${isParentActive ? "text-white" : ""}`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon size={18} strokeWidth={1.5} />
                  <span className="flex-1 text-left">{item.label}</span>
                  <ChevronDown size={14} className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="ml-4 pl-3 border-l border-slate-700 space-y-0.5 mt-0.5 mb-1">
                    {item.children.map((child) => {
                      const [childPath, childQuery] = child.to.split("?");
                      let isChildActive = childQuery
                        ? pathname === childPath && search.includes(childQuery)
                        : pathname === childPath && !search;
                      // Also highlight "Tous" when on a sub-route like /fournisseurs/:id
                      if (!isChildActive && !childQuery && childPath !== "/" && pathname.startsWith(childPath + "/")) {
                        const otherChildMatches = item.children.some(sib => {
                          const [sp] = sib.to.split("?");
                          return sp !== childPath && (pathname === sp || pathname.startsWith(sp + "/"));
                        });
                        if (!otherChildMatches) isChildActive = true;
                      }
                      return (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          onClick={onClose}
                          className={`sidebar-link text-xs py-1.5 ${isChildActive ? "active" : ""}`}
                          data-testid={`nav-sub-${child.label.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          {child.icon && <child.icon size={14} strokeWidth={1.5} />}
                          <span>{child.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={onClose}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? "active" : ""}`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <item.icon size={18} strokeWidth={1.5} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="px-3 py-2 border-t border-slate-700 flex-shrink-0 space-y-0">
        {user && !collapsed && (
          <SessionInfo createdAt={user.session_created_at} timeoutMinutes={user.session_idle_timeout_minutes}>
            <div className="flex items-center gap-2.5 mb-1 px-1.5 cursor-default">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-semibold">
                  {user.name?.[0] || "U"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white font-medium truncate">{user.name}</p>
                <p className="text-[11px] text-slate-400 truncate">{user.email}</p>
              </div>
            </div>
          </SessionInfo>
        )}
        {(user?.is_superadmin || user?.tenant_role === "owner" || user?.tenant_role === "admin") && (
          <>
            {user?.import_enabled && (
              <NavLink
                to="/import"
                onClick={onClose}
                title="Import données"
                className={({ isActive }) => `sidebar-link sidebar-link-compact ${collapsed ? "justify-center" : ""} ${isActive ? "active" : ""}`}
                data-testid="nav-import"
              >
                <Upload size={15} strokeWidth={1.5} />
                {!collapsed && <span>Import données</span>}
              </NavLink>
            )}
            <NavLink
              to="/admin"
              onClick={onClose}
              title="Administration"
              className={({ isActive }) => `sidebar-link sidebar-link-compact ${collapsed ? "justify-center" : ""} ${isActive ? "active" : ""}`}
              data-testid="nav-administration"
            >
              <Settings size={15} strokeWidth={1.5} />
              {!collapsed && <span>Administration</span>}
            </NavLink>
          </>
        )}
        <button
          onClick={onLogout}
          title="Déconnexion"
          className={`sidebar-link sidebar-link-compact w-full text-red-400 hover:text-red-300 hover:bg-red-500/10 ${collapsed ? "justify-center" : ""}`}
          data-testid="logout-btn"
        >
          <LogOut size={15} strokeWidth={1.5} />
          {!collapsed && <span>Déconnexion</span>}
        </button>
      </div>
    </div>
  );
}

export default function Sidebar({ user, onTenantSwitch }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [workflow, setWorkflow] = useState("devis_facture");
  const [taskPanelOpen, setTaskPanelOpen] = useState(false);
  const [taskCount, setTaskCount] = useState(0);
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  useSidebarAutoCollapse(1280);

  useEffect(() => {
    adminAPI.getSettings().then(r => setWorkflow(r.data?.workflow || "devis_facture")).catch(() => {});
  }, []);

  // Load pending task count
  useEffect(() => {
    const loadCount = () => {
      taskAPI.list(false, { background: true }).then(r => {
        const pending = (r.data || []).filter(t => !t.done).length;
        setTaskCount(pending);
      }).catch(() => {});
    };
    loadCount();
    const interval = setInterval(loadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const modules = user?.modules || ["marge", "ia", "facturation_prev", "stats", "contrats", "fournisseurs", "email"];
  const isSuperadmin = user?.is_superadmin || false;
  const tenantRole = user?.tenant_role || null;
  const navItems = getNavItems(workflow, modules, isSuperadmin, tenantRole);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (err) { console.error("Logout error:", err); /* user is redirected anyway */ }
    navigate("/login", { replace: true });
  };

  const handleTenantSwitch = () => {
    if (onTenantSwitch) onTenantSwitch();
    else window.location.href = "/";
  };

  const branding = user?.branding || {};
  const sidebarBg = branding.sidebar_color || undefined;
  const appName = branding.app_name || null;
  const brandLogo = branding.logo_url || null;

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col ${collapsed ? "w-16" : "w-64"} h-screen fixed left-0 top-0 z-40 overflow-hidden transition-[width] duration-200`}
        style={{ backgroundColor: sidebarBg || '#0f172a' }}
        data-testid="desktop-sidebar"
        data-collapsed={collapsed ? "1" : "0"}
      >
        <NavContent user={user} onLogout={handleLogout} navItems={navItems} onTenantSwitch={handleTenantSwitch} appName={appName} brandLogo={brandLogo} collapsed={collapsed} />
        <button
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? "Déplier le menu" : "Replier le menu"}
          className="absolute top-2 right-1 w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
        </button>
      </aside>

      {/* Mobile header + sheet */}
      <div className="lg:hidden glass-header fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 h-14">
        <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {appName || <img src="/logo_komptai_light.svg" alt="KomptAI" className="h-9 object-contain" />}
        </h1>
        <div className="flex items-center gap-1">
          <TaskButton count={taskCount} onClick={() => setTaskPanelOpen(true)} />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                {open ? <X size={20} /> : <Menu size={20} />}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64 border-none" style={{ backgroundColor: sidebarBg || '#0f172a' }}>
              <NavContent user={user} onLogout={handleLogout} onClose={() => setOpen(false)} navItems={navItems} onTenantSwitch={handleTenantSwitch} appName={appName} brandLogo={brandLogo} />
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Task Panel (shared desktop + mobile) */}
      <TaskPanel
        open={taskPanelOpen}
        onOpenChange={(v) => { setTaskPanelOpen(v); if (!v) taskAPI.list().then(r => setTaskCount((r.data || []).filter(t => !t.done).length)).catch(() => {}); }}
        isAdmin={user?.is_superadmin || user?.tenant_role === "owner" || user?.tenant_role === "admin"}
        currentUserId={user?.user_id}
      />
    </>
  );
}
